/**
 * What counts as a valid impression.
 *
 * These live in shared because both sides need them and neither may disagree:
 * the client uses them to decide when to *report* a view, and the server uses
 * the identical functions to decide whether to *count* it. A client that gets
 * this wrong is simply not believed.
 *
 * The rule underneath all of it: a file loading is not an impression. An
 * advertiser is invoiced for evidence somebody could actually see the thing.
 */
export const DEFAULT_VISIBILITY_RULES = {
    minVisibleMs: 1000,
    minScreenFraction: 0.5,
    dedupeWindowMinutes: 30
};
/**
 * Judges one view against the rules. Deterministic and side-effect free, so the
 * server can run it on a claim and get the same answer the client did.
 *
 * Checked in order of how cheap and how damning each test is.
 */
export function judgeView(claim, rules) {
    /* Impossible claims first. A client can send anything, and the numbers below
       are the ones a crafted request would inflate. A negative distance or a
       screen fraction above 1 is not a near miss — it is a client lying, and it
       is recorded as an anomaly rather than quietly clamped. */
    if (!Number.isFinite(claim.visibleMs) || claim.visibleMs < 0 ||
        !Number.isFinite(claim.distanceM) || claim.distanceM < 0 ||
        !Number.isFinite(claim.screenFraction) ||
        claim.screenFraction < 0 || claim.screenFraction > 1 ||
        // Nobody has had a tab open for a week of continuous visibility on one slot.
        claim.visibleMs > 10 * 60 * 1000) {
        return { valid: false, reason: 'impossible_claim' };
    }
    if (!claim.slotShowsOnDevice)
        return { valid: false, reason: 'wrong_device' };
    if (!claim.tabActive)
        return { valid: false, reason: 'tab_inactive' };
    if (!claim.unobstructed)
        return { valid: false, reason: 'obstructed' };
    if (claim.distanceM > claim.slotViewDistanceM)
        return { valid: false, reason: 'too_far' };
    if (claim.screenFraction < rules.minScreenFraction) {
        return { valid: false, reason: 'too_small_on_screen' };
    }
    if (claim.visibleMs < rules.minVisibleMs)
        return { valid: false, reason: 'too_brief' };
    return { valid: true, reason: null };
}
/** Plain-English reason, for the owner's dashboard rather than a log. */
export function describeReject(reason) {
    const text = {
        too_far: 'Further away than the slot allows',
        too_brief: 'On screen too briefly',
        too_small_on_screen: 'Too little of it was on screen',
        obstructed: 'Something was in the way',
        tab_inactive: 'The tab was not in front of the player',
        wrong_device: 'The slot is not shown on that device',
        impossible_claim: 'The client reported something impossible',
        duplicate: 'The same player already counted for this slot recently',
        player_cap: 'That player has reached the daily cap for this campaign',
        campaign_cap: 'The campaign has reached its cap'
    };
    return text[reason];
}
/**
 * How much of a rectangle sits inside the viewport, 0..1.
 *
 * Used by the client to fill in `screenFraction`. Kept here so the number the
 * server checks is defined in one place rather than described in prose.
 */
export function screenCoverage(rect, viewport) {
    const width = rect.right - rect.left;
    const height = rect.bottom - rect.top;
    if (width <= 0 || height <= 0)
        return 0;
    const visibleWidth = Math.max(0, Math.min(rect.right, viewport.width) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewport.height) - Math.max(rect.top, 0));
    return (visibleWidth * visibleHeight) / (width * height);
}
/**
 * What a campaign has earned over a period.
 *
 * Deliberately returns cents and never rounds a percentage: money that is
 * computed twice from a float eventually disagrees with itself.
 */
export function amountOwedCents(model, agreedAmountCents, counts) {
    switch (model) {
        case 'weekly':
            return agreedAmountCents * Math.max(0, Math.ceil(counts.weeks));
        case 'monthly':
            return agreedAmountCents * Math.max(0, Math.ceil(counts.months));
        case 'cpm':
            /* Pro-rata to the cent, which is the industry norm: 12,500 impressions at
               $4.00 per thousand is $50.00, not $48.00.
               An earlier version had a comment claiming this "never bills for a
               fraction of a thousand" — it did, and the two statements disagreed.
               Exact pro-rata is the defensible one: an advertiser can check it with a
               calculator, which is what matters when an invoice is queried. */
            return Math.floor((counts.validImpressions / 1000) * agreedAmountCents);
        case 'cpi':
            return counts.interactions * agreedAmountCents;
        case 'event':
        case 'custom':
            return agreedAmountCents;
    }
}
/** Australian GST, 10%, on a cents amount. */
export function gstCents(subtotalCents) {
    return Math.round(subtotalCents * 0.1);
}
// --- destination safety ----------------------------------------------------
/**
 * Whether a destination URL may be offered to a player.
 *
 * HTTPS only, no credentials in the URL, no IP literals, and no local or
 * private addresses — an advertisement is a link a child might tap, and the one
 * thing it must never do is reach something inside our own network.
 */
export function isAllowedDestination(raw) {
    let url;
    try {
        url = new URL(raw);
    }
    catch {
        return { ok: false, reason: 'That is not a valid web address.' };
    }
    if (url.protocol !== 'https:') {
        return { ok: false, reason: 'The address must start with https://' };
    }
    if (url.username !== '' || url.password !== '') {
        return { ok: false, reason: 'The address must not contain a username or password.' };
    }
    const host = url.hostname.toLowerCase();
    if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local')) {
        return { ok: false, reason: 'That address is not reachable from the public internet.' };
    }
    // IPv4 literal, including the private ranges behind it.
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
        return { ok: false, reason: 'Use a domain name rather than an IP address.' };
    }
    // IPv6 literal.
    if (host.startsWith('[')) {
        return { ok: false, reason: 'Use a domain name rather than an IP address.' };
    }
    if (!host.includes('.')) {
        return { ok: false, reason: 'That does not look like a public domain.' };
    }
    return { ok: true };
}
// --- artwork ---------------------------------------------------------------
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];
/**
 * Validates an uploaded asset against a slot.
 *
 * The aspect-ratio check has a tolerance because artwork is rarely exact, but
 * it exists: a 4:1 banner stretched into a 1:1 poster looks like a mistake by
 * whoever sold the slot, not by the advertiser who supplied the file.
 */
export function checkAsset(asset, slot, options = { allowVideo: false }) {
    const problems = [];
    if (asset.kind === 'video' && !options.allowVideo) {
        problems.push('Video advertising is switched off.');
    }
    if (!slot.acceptedTypes.includes(asset.mimeType)) {
        problems.push(`That slot accepts ${slot.acceptedTypes.join(', ')}.`);
    }
    if (asset.bytes > slot.maxFileBytes) {
        const mb = (slot.maxFileBytes / 1024 / 1024).toFixed(1);
        problems.push(`The file is too large — the limit for that slot is ${mb} MB.`);
    }
    if (asset.widthPx < 200 || asset.heightPx < 100) {
        problems.push('The artwork is too small to be readable in the world.');
    }
    const slotRatio = slot.widthM / slot.heightM;
    const assetRatio = asset.widthPx / asset.heightPx;
    const drift = Math.abs(assetRatio - slotRatio) / slotRatio;
    if (drift > 0.25) {
        problems.push(`That slot is about ${slotRatio.toFixed(2)}:1. Artwork at ${assetRatio.toFixed(2)}:1 ` +
            `would be noticeably stretched.`);
    }
    return { ok: problems.length === 0, problems };
}
/**
 * What still has to happen before a campaign can go in front of players.
 *
 * One definition, used twice on purpose: the advertiser portal shows this list
 * so somebody can finish their own campaign, and the review screen uses the
 * same list to refuse approving something incomplete. Two copies would drift,
 * and a drifted version means telling an advertiser they are ready and then
 * refusing them.
 */
export function campaignReadiness(facts) {
    const missing = [];
    if (facts.name.trim().length < 2)
        missing.push('Give the campaign a name.');
    if (facts.slotCount === 0)
        missing.push('Choose at least one advertising slot.');
    if (!facts.hasArtwork) {
        missing.push('Upload the artwork.');
    }
    else if (!facts.hasApprovedArtwork) {
        // Not a failure on the advertiser's part, so it is worded as a wait.
        missing.push('The artwork is waiting to be approved.');
    }
    if (facts.offerText.trim() === '') {
        missing.push('Write the offer — this is what a player reads when they tap the sign.');
    }
    /* The destination is checked even though the game never uses it, because the
       offers list does: somebody who keeps this offer will follow that link
       afterwards. A campaign with a bad link is a campaign that wastes the one
       click it earns. */
    if (facts.destinationUrl.trim() !== '') {
        const check = isAllowedDestination(facts.destinationUrl);
        if (!check.ok)
            missing.push(`The website link cannot be used: ${check.reason ?? 'it is not allowed'}.`);
    }
    if (!facts.targetMobile && !facts.targetDesktop) {
        missing.push('Choose at least one of phone or computer.');
    }
    const starts = Date.parse(facts.startsOn);
    const ends = Date.parse(facts.endsOn);
    if (!Number.isFinite(starts) || !Number.isFinite(ends)) {
        missing.push('Set the start and finish dates.');
    }
    else if (ends < starts) {
        missing.push('The finish date is before the start date.');
    }
    // A booking nobody agreed a price for is not a booking.
    if (facts.agreedAmountCents <= 0 && facts.billingModel !== 'custom') {
        missing.push('An amount has not been agreed yet.');
    }
    return { ready: missing.length === 0, missing };
}
/** Which statuses an advertiser may still edit. */
export function isEditableByAdvertiser(status) {
    // Once it is submitted it is somebody else's to look at; once it has run,
    // changing it would rewrite what was already shown and already billed.
    return status === 'draft' || status === 'rejected';
}
/** The moves allowed out of each status, so no screen can invent one. */
export const CAMPAIGN_TRANSITIONS = {
    draft: ['submitted', 'cancelled'],
    submitted: ['approved', 'rejected', 'cancelled'],
    approved: ['live', 'cancelled'],
    live: ['paused', 'finished', 'cancelled'],
    paused: ['live', 'finished', 'cancelled'],
    rejected: ['draft', 'cancelled'],
    finished: [],
    cancelled: []
};
export function canTransition(from, to) {
    return CAMPAIGN_TRANSITIONS[from].includes(to);
}
//# sourceMappingURL=ads.js.map